import { useState, useEffect } from 'react';

interface SchemaStatus {
  id: string;
  color: string;
  order: number;
  description: string;
}

interface SchemaPriority {
  id: string;
  color: string;
  order: number;
  description: string;
}

interface SchemaType {
  id: string;
  icon: string;
  color: string;
  description: string;
}

interface EpicLinking {
  enabled: boolean;
  linkableTypes: string[];
  progressCalculation: string;
  displayFormat: string;
  description: string;
}

interface KanbanSchema {
  statuses: SchemaStatus[];
  priorities: SchemaPriority[];
  types: SchemaType[];
  epicLinking?: EpicLinking;
  metadata: {
    version: string;
    lastUpdated: string;
    description: string;
  };
}

interface UseKanbanSchemaResult {
  schema: KanbanSchema | null;
  loading: boolean;
  error: string | null;
  priorities: string[];
  types: string[];
  statuses: string[];
  getPriorityColor: (priority: string) => string;
  getTypeIcon: (type: string) => string;
  getTypeColor: (type: string) => string;
  isEpicLinkingEnabled: boolean;
  getLinkableTypes: () => string[];
  canLinkToEpic: (type: string) => boolean;
}

export const useKanbanSchema = (): UseKanbanSchemaResult => {
  const [schema, setSchema] = useState<KanbanSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/api/kanban/schema');
        if (!response.ok) {
          throw new Error(`Failed to fetch schema: ${response.status}`);
        }
        
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Failed to load schema');
        }
        
        // Transform API schema structure to frontend structure
        const apiSchema = result.data;
        const transformedSchema = {
          statuses: apiSchema.field_definitions?.status?.values?.map((value: string, index: number) => ({
            id: value,
            color: '#666666', // Default color
            order: index,
            description: value
          })) || [],
          priorities: apiSchema.field_definitions?.priority?.values?.map((value: string, index: number) => {
            const priorityMap: Record<string, { color: string, description: string }> = {
              'Critical': { color: '#FF3B30', description: 'Urgent and critical priority' },
              'High': { color: '#FF9500', description: 'Important and urgent' },
              'Medium': { color: '#007AFF', description: 'Standard priority' },
              'Low': { color: '#34C759', description: 'Nice to have when time permits' }
            };
            const mapping = priorityMap[value] || { color: '#007AFF', description: value };
            return {
              id: value,
              color: mapping.color,
              order: index,
              description: mapping.description
            };
          }) || [],
          types: apiSchema.field_definitions?.type?.values?.map((value: string, _index: number) => {
            const typeMap: Record<string, { icon: string, color: string, description: string }> = {
              'Epic': { icon: '🎯', color: '#AF52DE', description: 'Large multi-phase work' },
              'Feature': { icon: '⭐', color: '#34C759', description: 'New functionality' },
              'Task': { icon: '📋', color: '#007AFF', description: 'General work items' },
              'Bug': { icon: '🐛', color: '#FF3B30', description: 'Issues to fix' },
              'Enhancement': { icon: '🔧', color: '#FF9500', description: 'Improvements to existing features' },
              'Spike': { icon: '🔬', color: '#5856D6', description: 'Research and investigation' },
              'Chore': { icon: '🧹', color: '#8E8E93', description: 'Maintenance and cleanup tasks' }
            };
            const mapping = typeMap[value] || { icon: '📋', color: '#007AFF', description: value };
            return {
              id: value,
              icon: mapping.icon,
              color: mapping.color,
              description: mapping.description
            };
          }) || [],
          metadata: apiSchema.meta || {
            version: '1.0',
            lastUpdated: new Date().toISOString(),
            description: 'API schema'
          }
        };
        
        setSchema(transformedSchema);
      } catch (err) {
        console.error('Error fetching Kanban schema:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to default values if schema fetch fails
        setSchema({
          statuses: [
            { id: 'Not Started', color: '#666666', order: 0, description: 'Work has not begun' },
            { id: 'In Progress', color: '#34C759', order: 1, description: 'Currently being worked on' },
            { id: 'Completed', color: '#30D158', order: 2, description: 'Work is finished' }
          ],
          priorities: [
            { id: 'High', color: '#FF9500', order: 0, description: 'Important and urgent' },
            { id: 'Medium', color: '#007AFF', order: 1, description: 'Standard priority' },
            { id: 'Low', color: '#34C759', order: 2, description: 'Nice to have when time permits' }
          ],
          types: [
            { id: 'Task', icon: '📋', color: '#007AFF', description: 'General work items' },
            { id: 'Feature', icon: '⭐', color: '#34C759', description: 'New functionality' },
            { id: 'Bug', icon: '🐛', color: '#FF3B30', description: 'Issues to fix' },
            { id: 'Enhancement', icon: '🔧', color: '#FF9500', description: 'Improvements to existing features' },
            { id: 'Epic', icon: '🎯', color: '#AF52DE', description: 'Large multi-phase work' }
          ],
          metadata: {
            version: 'fallback',
            lastUpdated: new Date().toISOString(),
            description: 'Fallback schema due to fetch error'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, []);

  // Computed values
  const priorities = schema?.priorities?.map(p => p.id) || ['Low', 'Medium', 'High', 'Critical'];
  const types = schema?.types?.map(t => t.id) || ['Task', 'Feature', 'Bug', 'Enhancement', 'Epic'];
  const statuses = schema?.statuses?.map(s => s.id) || ['Not Started', 'In Progress', 'Completed'];

  // Helper functions
  const getPriorityColor = (priority: string): string => {
    const priorityItem = schema?.priorities.find(p => p.id === priority);
    return priorityItem?.color || 'var(--text-tertiary)';
  };

  const getTypeIcon = (type: string): string => {
    const typeItem = schema?.types.find(t => t.id === type);
    return typeItem?.icon || '📋';
  };

  const getTypeColor = (type: string): string => {
    const typeItem = schema?.types.find(t => t.id === type);
    return typeItem?.color || 'var(--text-tertiary)';
  };

  // Epic linking helper functions
  const isEpicLinkingEnabled = schema?.epicLinking?.enabled || false;
  
  const getLinkableTypes = (): string[] => {
    return schema?.epicLinking?.linkableTypes || [];
  };
  
  const canLinkToEpic = (type: string): boolean => {
    if (!isEpicLinkingEnabled) return false;
    return getLinkableTypes().includes(type);
  };

  return {
    schema,
    loading,
    error,
    priorities,
    types,
    statuses,
    getPriorityColor,
    getTypeIcon,
    getTypeColor,
    isEpicLinkingEnabled,
    getLinkableTypes,
    canLinkToEpic
  };
};